// src/components/themes/default/admin/BranchBarcodeScannerTab.tsx
"use client";

import { useState, useRef, useEffect } from "react";
import Swal from "sweetalert2";
import {
  Barcode,
  Scale,
  Printer,
  CheckCircle2,
  Receipt,
  Volume2,
  Package,
  Zap,
} from "lucide-react";

interface BranchBarcodeScannerTabProps {
  branchInfo: any;
  subdomain: string;
}

export default function BranchBarcodeScannerTab({
  branchInfo,
  subdomain,
}: BranchBarcodeScannerTabProps) {
  const [barcodeInput, setBarcodeInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [lastSale, setLastSale] = useState<any | null>(null);
  const [recentSales, setRecentSales] = useState<any[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const playBeep = (isScale: boolean) => {
    try {
      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.frequency.value = isScale ? 2200 : 1700;
      gain.gain.value = 0.15;
      osc.start();
      setTimeout(() => {
        osc.stop();
        audioCtx.close();
      }, 100);
    } catch {}
  };

  const getApiUrl = (endpoint: string) => {
    const isLocal =
      typeof window !== "undefined" &&
      (window.location.hostname.includes("localhost") ||
        window.location.hostname === "127.0.0.1");
    return isLocal ? `http://${subdomain}.localhost:8000${endpoint}` : endpoint;
  };

  const handleScanSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const code = barcodeInput.trim();
    if (!code) return;

    setLoading(true);
    try {
      const storageKey = `branch_auth_${subdomain}_${branchInfo?.slug}`;
      const authData = JSON.parse(localStorage.getItem(storageKey) || "{}");
      const token = authData.token || "";

      const res = await fetch(getApiUrl("/api/v1/inventory/barcode-stock-deduct/"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Branch-ID": String(branchInfo?.id),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ barcode: code, branch_id: branchInfo?.id }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Scan failed!");
      }

      const isScale = data.item_type === "scale";
      playBeep(isScale);
      setLastSale(data.data);
      setRecentSales((prev) => [data.data, ...prev.slice(0, 7)]);
      setBarcodeInput("");

      Swal.fire({
        position: "top",
        icon: "success",
        title: isScale ? "⚖️ Weighted Item Sold!" : "📦 Fixed Item Sold!",
        text: `${data.data.product_name} (${data.data.quantity_display}) - ৳${data.data.collected_amount}`,
        showConfirmButton: false,
        timer: 1600,
        background: "#090d16",
        color: "#ffffff",
      });
    } catch (err: any) {
      Swal.fire({
        position: "top",
        icon: "error",
        title: "Scan Error",
        text: err.message,
        background: "#090d16",
        color: "#ffffff",
      });
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handlePrintReceipt = (sale: any) => {
    if (!sale) return;
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const isScale = sale.item_type === "scale";

    printWindow.document.write(`
      <html>
        <head>
          <title>POS Receipt - ${sale.pos_order_number}</title>
          <style>
            @page { size: 80mm auto; margin: 4mm; }
            body { font-family: monospace; font-size: 13px; color: #000; width: 72mm; margin: 0 auto; text-align: center; }
            .header { border-bottom: 1px dashed #000; padding-bottom: 6px; margin-bottom: 8px; }
            .title { font-size: 16px; font-weight: bold; text-transform: uppercase; }
            .meta { font-size: 11px; margin-top: 3px; }
            .item-row { display: flex; justify-content: space-between; margin: 6px 0; font-size: 13px; text-align: left; }
            .divider { border-top: 1px dashed #000; margin: 8px 0; }
            .total-row { display: flex; justify-content: space-between; font-size: 15px; font-weight: bold; }
            .footer { margin-top: 12px; font-size: 10px; border-top: 1px dashed #000; padding-top: 6px; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="title">${branchInfo?.name || "OUTLET"}</div>
            <div class="meta">${branchInfo?.address || "Dhaka, Bangladesh"}</div>
            <div class="meta">Order: #${sale.pos_order_number}</div>
            <div class="meta">Date: ${sale.date}</div>
          </div>
          
          <div class="item-row">
            <span>${sale.product_name}</span>
            <span>৳${sale.collected_amount}</span>
          </div>
          <div style="text-align: left; font-size: 11px; color: #444;">
            ${isScale ? `Rate: ৳${sale.unit_price}/kg x${sale.quantity_display}` : `Unit Price: ৳${sale.unit_price} x${sale.quantity_display}`}
          </div>

          <div class="divider"></div>
          <div class="total-row">
            <span>TOTAL PAID:</span>
            <span>৳${sale.collected_amount}</span>
          </div>
          <div style="text-align: left; font-size: 11px; margin-top: 3px;">
            Payment: CASH (${isScale ? "SCALE POS" : "FIXED COUNTER"})
          </div>

          <div class="footer">
            Thank you for shopping with us!<br/>
            Powered by Xylo SaaS Retail
          </div>
          <script>window.onload = function() { window.print(); window.close(); }</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="space-y-6 animate-fadeIn font-sans text-white">
      <div className="p-6 rounded-3xl bg-slate-900 border border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-xl">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-2xl bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
            <Zap className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-lg sm:text-xl font-black">
              Smart Dual POS Scanner
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">
              {branchInfo?.name} — Auto-detects <strong>Fixed Barcode</strong> & <strong>Scale Sticker</strong>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="px-3 py-1.5 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-400 text-[11px] font-bold flex items-center gap-1.5">
            <Package className="w-3.5 h-3.5" />
            <span>Fixed Piece (1 pc)</span>
          </span>
          <span className="px-3 py-1.5 rounded-xl bg-purple-500/10 border border-purple-500/20 text-purple-400 text-[11px] font-bold flex items-center gap-1.5">
            <Scale className="w-3.5 h-3.5" />
            <span>Scale Sticker (Weight)</span>
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <div className="lg:col-span-7 space-y-5">
          <div className="p-6 rounded-3xl bg-slate-900 border border-slate-800 shadow-xl space-y-4">
            <form onSubmit={handleScanSubmit} className="space-y-3">
              <label className="block text-xs font-black uppercase tracking-wider text-slate-400 flex items-center justify-between">
                <span>Universal Barcode Reader</span>
                <span className="text-emerald-400 lowercase font-mono">auto detects item type</span>
              </label>

              <div className="relative">
                <Barcode className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-emerald-400" />
                <input
                  ref={inputRef}
                  type="text"
                  autoFocus
                  value={barcodeInput}
                  onChange={(e) => setBarcodeInput(e.target.value)}
                  placeholder="Scan item or scale sticker (e.g. 2134221015456)..."
                  className="w-full pl-12 pr-28 py-3.5 rounded-2xl bg-slate-950 border-2 border-emerald-500/40 focus:border-emerald-500 text-white font-mono text-base font-bold outline-none shadow-inner transition-all placeholder:text-slate-600"
                />
                <button
                  type="submit"
                  disabled={loading || !barcodeInput.trim()}
                  className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold transition-all cursor-pointer disabled:opacity-40"
                >
                  {loading ? "..." : "Confirm"}
                </button>
              </div>
            </form>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              <div className="p-3.5 rounded-2xl bg-slate-950 border border-slate-800 text-xs text-slate-400 space-y-1">
                <span className="font-bold text-blue-400 flex items-center gap-1.5">
                  <Package className="w-3.5 h-3.5" />
                  <span>Fixed Product</span>
                </span>
                <p className="text-[11px] leading-relaxed">
                  Scans regular barcode and deducts 1 unit stock.
                </p>
              </div>

              <div className="p-3.5 rounded-2xl bg-slate-950 border border-slate-800 text-xs text-slate-400 space-y-1">
                <span className="font-bold text-purple-400 flex items-center gap-1.5">
                  <Scale className="w-3.5 h-3.5" />
                  <span>Weighing Scale</span>
                </span>
                <p className="text-[11px] leading-relaxed">
                  Decodes weight from scale sticker and computes price by rate.
                </p>
              </div>
            </div>
          </div>

          {lastSale && (
            <div className="p-6 rounded-3xl bg-gradient-to-br from-emerald-950/40 via-slate-900 to-slate-900 border border-emerald-500/40 shadow-xl space-y-4 animate-fadeIn">
              <div className="flex items-center justify-between pb-3 border-b border-slate-800">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  <h3 className="text-sm font-black text-white">Last Completed Sale</h3>
                  <span
                    className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider ${
                      lastSale.item_type === "scale"
                        ? "bg-purple-500/20 text-purple-300 border border-purple-500/30"
                        : "bg-blue-500/20 text-blue-300 border border-blue-500/30"
                    }`}
                  >
                    {lastSale.item_type === "scale" ? "Weighing Scale" : "Fixed Unit"}
                  </span>
                </div>
                <span className="text-xs font-mono text-emerald-400 font-bold">
                  #{lastSale.pos_order_number}
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                <div className="p-3 rounded-2xl bg-slate-950/70 border border-slate-800">
                  <span className="text-[10px] text-slate-400 block font-bold">Item Name</span>
                  <span className="text-sm font-black text-white truncate block mt-0.5">
                    {lastSale.product_name}
                  </span>
                </div>

                <div className="p-3 rounded-2xl bg-slate-950/70 border border-slate-800">
                  <span className="text-[10px] text-slate-400 block font-bold">Quantity / Weight</span>
                  <span className="text-sm font-black text-emerald-400 font-mono block mt-0.5">
                    {lastSale.quantity_display}
                  </span>
                </div>

                <div className="p-3 rounded-2xl bg-slate-950/70 border border-slate-800 col-span-2 sm:col-span-1">
                  <span className="text-[10px] text-slate-400 block font-bold">Collected Amount</span>
                  <span className="text-base font-black text-emerald-400 font-mono block mt-0.5">
                    ৳{lastSale.collected_amount}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => handlePrintReceipt(lastSale)}
                  className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold flex items-center gap-2 cursor-pointer shadow-md transition-all active:scale-95"
                >
                  <Printer className="w-4 h-4" />
                  <span>Print Receipt</span>
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-5 p-6 rounded-3xl bg-slate-900 border border-slate-800 shadow-xl space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-slate-800">
            <div className="flex items-center gap-2">
              <Receipt className="w-4 h-4 text-emerald-400" />
              <h3 className="text-xs font-black uppercase tracking-wider text-slate-300">
                Recent Counter Sales
              </h3>
            </div>
            <span className="text-[10px] font-mono text-slate-500">Live Session</span>
          </div>

          <div className="space-y-2.5 max-h-[460px] overflow-y-auto">
            {recentSales.length === 0 ? (
              <div className="text-center py-12 text-slate-500 text-xs">
                <Barcode className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p>No sales scanned yet.</p>
              </div>
            ) : (
              recentSales.map((sale, idx) => (
                <div
                  key={idx}
                  className="p-3 rounded-2xl bg-slate-950 border border-slate-800/80 flex items-center justify-between text-xs hover:border-slate-700 transition-colors"
                >
                  <div>
                    <div className="flex items-center gap-1.5">
                      <h4 className="font-bold text-white truncate max-w-[150px]">
                        {sale.product_name}
                      </h4>
                      <span
                        className={`text-[8px] font-bold px-1.5 py-0.2 rounded uppercase ${
                          sale.item_type === "scale"
                            ? "bg-purple-500/20 text-purple-400"
                            : "bg-blue-500/20 text-blue-400"
                        }`}
                      >
                        {sale.item_type === "scale" ? "Scale" : "Piece"}
                      </span>
                    </div>
                    <span className="text-[10px] text-slate-400 font-mono">
                      {sale.quantity_display} • Rate: ৳{sale.unit_price}
                    </span>
                  </div>

                  <div className="text-right">
                    <span className="font-mono font-black text-emerald-400 block">
                      ৳{sale.collected_amount}
                    </span>
                    <button
                      type="button"
                      onClick={() => handlePrintReceipt(sale)}
                      className="text-[10px] text-slate-400 hover:text-white underline cursor-pointer"
                    >
                      Print
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}